import { useState } from "react";
import * as React from "react";
import ReactMarkdown from "react-markdown";
import Carousel from "react-bootstrap/Carousel";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import dayjs, { Dayjs } from "dayjs";
import rehypeRaw from "rehype-raw";
import { FiExternalLink } from "react-icons/fi";
import { getTitle, getContent, getAutorLink } from "../utils/helpers.ts";

import SnowConditionEntry from "./SnowConditionEntry.tsx";
import AvalancheConditionEntry from "./AvalancheConditionEntry.tsx";
import RegionImageOverlay from "./RegionImageOverlay.tsx";
import GOMRating from "./GOMRating.tsx";
import {
  formatTimestampToUserLocale,
  getGradeOfManageability,
  getGradeOfManageabilityLabel,
} from "../utils/helpers.ts";
import { PiSnowflakeLight } from "react-icons/pi";
import { CgDanger } from "react-icons/cg";

import "../snowreport.css";
import "bootstrap/dist/css/bootstrap.min.css";
export const dbEndpoint = "https://backend.schnee.report";

const ReportPaperElem = styled(Paper)(({ theme }) => ({
  width: "90%",
  margin: "20px",
  padding: theme.spacing(2),
  ...theme.typography.body2,
  textAlign: "center",
}));

interface ReportPaperProps {
  entry: any;
  regions?: any;
  regionOptions?: any;
  selectedRegionImages?: any;
}

function ReportPaper({
  entry,
  regions,
  regionOptions,
  selectedRegionImages,
}: ReportPaperProps) {
  const [index, setIndex] = useState<number>(0);

  const handleSelect = (selectedIndex: number) => {
    setIndex(selectedIndex);
  };

  // Berechne die Differenz in Tagen
  const reportDate = dayjs(entry.valid_from);
  const today = dayjs();
  const daysOld = today.diff(reportDate, "day");

  // Bestimme den Warnungstext basierend auf der Anzahl der Tage
  let warningMessage: string | null = null;
  if (daysOld > 14) {
    warningMessage = "Dieser Schneereport ist stark veraltet";
  } else if (daysOld > 7) {
    warningMessage = "Dieser Schneereport ist veraltet";
  }
  const autorUrl = getAutorLink(
    entry.createdBy.firstname,
    entry.createdBy.lastname
  );
  const valid_from_str = entry.valid_from
    ? "Schneereport für " + formatTimestampToUserLocale(entry.valid_from)
    : "";
  const valid_until_str = entry.valid_until
    ? " bis voraussichtlich " + formatTimestampToUserLocale(entry.valid_until)
    : "";

  return (
    <>
      <div id={entry.documentId} style={{ scrollMarginTop: "50px" }}></div>
      <ReportPaperElem elevation={6}>
        <div className="report-box">
          {warningMessage && (
            <Alert
              severity={daysOld > 14 ? "error" : "warning"}
              style={{ marginBottom: "20px", justifyContent: "center" }}
            >
              {warningMessage}
            </Alert>
          )}
          <Grid container>
            <Grid
              container
              size={{ xs: 12, md: 6 }}
              className="d-flex align-items-center justify-content-center"
            >
              <Grid
                container
                size={{ xs: 12 }}
                rowSpacing={0}
                className="d-flex align-items-center justify-content-center"
              >
                <Grid
                  size={{ xs: 12 }}
                  className="d-flex align-items-start justify-content-center"
                >
                  <h3 className="report-heading">
                    {valid_from_str}
                    <br></br>
                    {valid_until_str}
                  </h3>
                </Grid>
                <Grid
                  size={{ xs: 12 }}
                  className="d-flex align-items-start justify-content-center"
                >
                  <h3 className="report-autor-heading">
                    {"von"} {entry.createdBy.firstname}{" "}
                    {entry.createdBy.lastname}
                    {autorUrl && (
                      <a href={autorUrl} target="_blank" rel="noreferrer">
                        <FiExternalLink
                          size={"18px"}
                          style={{
                            color: "gray",
                            paddingBottom: "3px",
                            marginLeft: "5px",
                            cursor: "pointer",
                          }}
                        />
                      </a>
                    )}
                  </h3>
                </Grid>
              </Grid>
              <Grid
                size={{ xs: 12 }}
                className="d-flex align-items-start justify-content-center"
              >
                <h3 className="report-title">{getTitle(entry)}</h3>
              </Grid>
              <Grid size={{ xs: 12 }} className="justify-content-center">
                <div className="d-flex align-items-center justify-content-center">
                  {regions.map((regionId) => {
                    const region = regionOptions.find(
                      (r) => r.documentId === regionId
                    );
                    return region ? (
                      <Chip
                        key={regionId}
                        label={region.name}
                        variant="outlined"
                        style={{ margin: "10px" }}
                      />
                    ) : null;
                  })}
                </div>
              </Grid>
              {!!getGradeOfManageability(entry.grade_of_manageability) && (
                <Grid size={{ xs: 12 }}>
                  <div className="d-flex align-items-center justify-content-center">
                    <GOMRating
                      fontSize={"16px"}
                      label={getGradeOfManageabilityLabel(
                        entry.grade_of_manageability
                      )}
                      value={getGradeOfManageability(
                        entry.grade_of_manageability
                      )}
                    />
                  </div>
                </Grid>
              )}
            </Grid>
            <Grid
              size={{ xs: 12, md: 6 }}
              className="d-flex justify-content-center"
            >
              <RegionImageOverlay imageSources={selectedRegionImages} canvasClass={"region-image"} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <hr style={{ border: "1px solid black", opacity: ".5" }} />
            </Grid>
            <Grid
              size={{ xs: 12, md: 12 }}
              className="d-flex align-items-center justify-content-center"
            >
              <div style={{ width: "100%" }}>
                <Grid container>
                  {!!entry.snow && !!entry.snow.length && (
                    <>
                      <Grid
                        size={{ xs: 1, md: 1 }}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <div className="d-flex align-items-center justify-content-center snow-grid">
                          <Chip
                            sx={{
                              transform: "rotate(-90deg)",
                              transformOrigin: "center",
                              minWidth: "100px",
                              backgroundColor: "#e0b3b3",
                            }}
                            icon={<PiSnowflakeLight size={"20px"} />}
                            label={"Schnee"}
                            size="string"
                          />
                        </div>
                      </Grid>
                      <Grid size={{ xs: 11, sm: 11 }}>
                        {Object.entries(entry.snowData).map(
                          (
                            [snowCondition, snowConditionData]: [any, any],
                            idx
                          ) => (
                            <SnowConditionEntry
                              key={idx}
                              isFirstEntry={idx === 0}
                              snowType={snowCondition as any}
                              snowConditionData={snowConditionData}
                              cssIdentifier={entry.documentId + "-s" + idx}
                            />
                          )
                        )}
                      </Grid>
                      <Grid
                        size={{ xs: 12 }}
                        style={{ padding: "0px", marginTop: "10px" }}
                      >
                        <hr
                          style={{
                            opacity: ".5",
                            border: "1px solid black",
                            margin: "0 0 10px 0",
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  {!!entry.avalanche && !!entry.avalanche.length && (
                    <>
                      <Grid
                        size={{ xs: 1, md: 1 }}
                        className="d-flex align-items-center justify-content-center"
                      >
                        <div className="d-flex align-items-center justify-content-center avalanche-grid">
                          <Chip
                            sx={{
                              transform: "rotate(-90deg)",
                              transformOrigin: "center",
                              minWidth: "100px",
                              backgroundColor: "#ebd3f2",
                            }}
                            icon={<CgDanger size={"20px"} />}
                            label={"Lawinen"}
                            size="string"
                          />
                        </div>
                      </Grid>
                      <Grid size={{ xs: 11, sm: 11 }}>
                        {Object.entries(entry.avalancheData).map(
                          (
                            [avalancheCondition, avalancheConditionData]: [
                              any,
                              any
                            ],
                            idx
                          ) => (
                            <AvalancheConditionEntry
                              key={idx}
                              isFirstEntry={idx === 0}
                              avalancheType={avalancheCondition as any}
                              avalancheConditionData={avalancheConditionData}
                              cssIdentifier={entry.documentId + "-a" + idx}
                            />
                          )
                        )}
                      </Grid>
                      <Grid
                        size={{ xs: 12 }}
                        style={{ padding: "0px", marginTop: "10px" }}
                      >
                        <hr
                          style={{
                            opacity: ".5",
                            border: "1px solid black",
                            margin: "0 0 10px 0",
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className="report-text">
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>
            {getContent(entry)}
          </ReactMarkdown>
          <div className="report-images">
            {!!(entry.images && entry.images.length) && (
              <Carousel
                controls={entry.images.length > 1}
                indicators={false}
                interval={null}
                activeIndex={index}
                onSelect={handleSelect}
              >
                {entry.images.map((image: any, idx: number) => {
                  const imageUrl =
                    image.picture?.formats?.large?.url || image.picture?.url;
                  return imageUrl ? (
                    <Carousel.Item key={idx}>
                      <img
                        src={dbEndpoint + imageUrl}
                        alt={`Bild ${idx + 1}`}
                        style={{
                          objectFit: "contain",
                          width: "87%",
                          maxHeight: "550px",
                        }}
                      />
                      <div className="custom-caption">
                        <p></p>
                        <p>{image.caption}</p>
                      </div>
                    </Carousel.Item>
                  ) : null;
                })}
              </Carousel>
            )}
          </div>
        </div>
      </ReportPaperElem>
    </>
  );
}

export default ReportPaper;
